import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from "@angular/core";
import { StateService, UIRouterGlobals } from "@uirouter/core";
import { debounce, orderBy } from "lodash";
import { finalize } from "rxjs/operators";
import { environment as appEnv } from "../../../environments/environment";
import { ErrorRateListService } from "../../error-rate-list/error-rate-list.service";
import { RoutesStatsService } from "../../models/routes-stats.service";
import { RoutesService } from "../../models/routes.service";
import { RememberService } from "../../remember/remember.service";
import { RoundTimeService } from "../../round-time/round-time.service";
import { TimeDurAgoService } from "../../time-dur-ago/time-dur-ago.service";
import { routeTableSampleRoutes } from "./sample-data/routes";
import { routeTableSampleRoutesStats } from "./sample-data/routes-stats";
import { routeTableSampleTotalStatus } from "./sample-data/total-status";

@Component({
  selector: "ab-route-table",
  templateUrl: "./route-table.component.html",
})
export class RouteTableComponent implements OnChanges, OnInit {
  @Input() public project: any;
  @Input() public dur: string;
  @Input() public lastN: number;
  @Input() public timeDurAgo: string;
  @Input() public httpMethod: string;
  @Input() public environment: string;
  @Input() public featureDisabled: boolean;
  @Input() public currentTab: string;
  @Input() public queryPage: number;
  @Input() public sortQueryColumn: string;
  @Input() public sortQueryDesc: boolean;
  @Input() public routeSearch: string;
  @Input() public querySearch: string;
  @Input() public queueSearch: string;
  @Input() public queuePage: number;
  @Input() public sortQueueColumn: string;
  @Input() public sortQueueDesc: boolean;
  @Output() public loadingChanged = new EventEmitter<number>();

  public appEnv = appEnv;
  public currentPage: number;
  public sortColumn: string;
  public sortDesc: boolean;
  public loadingQueries: number;
  public apdexT: number;
  public startTime: number;
  public totalPages: number;
  public routes: any;
  public loadingRoutes: boolean;
  public totalRoutes: any;
  public loadingRoutesStats: boolean;
  public routesStats: any;
  public totalStatus: any;
  public debounceSearchRoute = debounce(() => {
    this.searchRoute();
  }, 1500);
  public errorRateList: number[];

  private DEFAULT_SORT_COLUMN = "serverError.count";
  private REMEMBER_SORT_COLUMN = "projectPerformance.routes.sortColumn";
  private REMEMBER_SORT_DESC = "projectPerformance.routes.sortDesc";
  private PAGE_SIZE = 20;

  constructor(
    private $state: StateService,
    private $uiRouterGlobals: UIRouterGlobals,
    private errorRateListService: ErrorRateListService,
    private rememberService: RememberService,
    private roundTimeService: RoundTimeService,
    private routesService: RoutesService,
    private routesStatsService: RoutesStatsService,
    private timeDurAgoService: TimeDurAgoService,
  ) {}

  public ngOnInit() {
    this.currentPage = Number(this.$uiRouterGlobals.params.page || 1);
    this.sortColumn =
      this.$uiRouterGlobals.params.sortColumn ||
      this.rememberService.get(
        this.REMEMBER_SORT_COLUMN,
        this.DEFAULT_SORT_COLUMN,
      );
    this.sortDesc =
      this.$uiRouterGlobals.params.sortDesc ||
      this.rememberService.get(
        this.REMEMBER_SORT_DESC,
        this.defaultSortDesc(this.sortColumn),
      );

    this.$state.go(this.$uiRouterGlobals.current.name, {
      sortColumn: this.sortColumn,
      sortDesc: this.sortDesc,
      page: this.currentPage,
    });

    this.setRoutes();
    this.setRoutesStats();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (!changes.timeDurAgo && !changes.httpMethod && !changes.environment) {
      return;
    }

    this.loadingQueries = 0;

    // Apdex Threshold in ms.
    this.apdexT = this.project.apdexThreshold * 1000;
    this.startTime = Math.round(new Date(this.timeDurAgo).getTime() / 1000);

    if (
      this.firstChange(changes.timeDurAgo) &&
      this.firstChange(changes.httpMethod)
    ) {
      return;
    }

    this.loadPage(1);
    this.setRoutesStats();
  }

  public sortBy(column: string) {
    this.sortDesc =
      column === this.sortColumn
        ? !this.sortDesc
        : this.defaultSortDesc(column);
    this.rememberService.set(this.REMEMBER_SORT_DESC, this.sortDesc);

    this.sortColumn = column;
    this.rememberService.set(this.REMEMBER_SORT_COLUMN, this.sortColumn);

    const params = {
      sortColumn: this.sortColumn,
      sortDesc: this.sortDesc,
    } as any;

    if (this.totalPages > 1) {
      this.currentPage = 1;
      params.page = 1;
      this.setRoutes();
    } else {
      this.routes = this.sortRoutes(
        this.routes,
        this.sortColumn,
        this.sortDesc,
      );
    }

    this.$state.go(this.$uiRouterGlobals.current.name, params);
  }

  public loadPage(page: number) {
    this.currentPage = page;
    this.$state.go(this.$uiRouterGlobals.current.name, { page });
    this.setRoutes();
  }

  public trackBy(_index: number, route: any) {
    return route.routeId;
  }

  private searchRoute() {
    if (this.featureDisabled) {
      return;
    }
    this.currentPage = 1;
    this.$state.go(this.$uiRouterGlobals.current.name, {
      routeSearch: this.routeSearch,
      page: 1,
    });
    this.setRoutes();
  }

  private attachRequestRates(routes: any) {
    const time = this.timeDurAgoService.inMinutes(this.dur, this.lastN);

    for (const route of routes) {
      route.total.requestRate = route.total.count / time;
    }

    return routes;
  }

  private responseTimeClass(responseTime: number) {
    if (responseTime <= this.apdexT) {
      return;
    } else if (responseTime <= this.apdexT * 4) {
      return "tolerating";
    } else {
      return "frustrated";
    }
  }

  private attachResponseTimeClasses(routes: any) {
    if (this.apdexT === 0) {
      return routes;
    }

    for (const route of routes) {
      route.total.p50ResponseTimeClass = this.responseTimeClass(
        route.total.p50,
      );
      route.total.p95ResponseTimeClass = this.responseTimeClass(
        route.total.p95,
      );
    }

    return routes;
  }

  private attachResponseCodesBarData(routes: any) {
    for (const route of routes) {
      route.total.responseCodesBarData = {
        success: route.success.count,
        redirection: route.redirection.count,
        clientError: route.clientError.count,
        serverError: route.serverError.count,
      };
    }

    return routes;
  }

  // Needed to support saved sorting preference in the localStorage.
  private orderBy() {
    let order = "";
    switch (this.sortColumn) {
      case "clientError.count":
        order = "client_error.count";
        break;
      case "serverError.count":
        order = "server_error.count";
        break;
      case "noticeCount":
        order = "notice_count";
        break;
      case "total.requestRate":
        order = "total.count";
        break;
      default:
        order = this.sortColumn;
    }
    if (String(this.sortDesc) === "true") {
      order += " desc";
    }

    return order;
  }

  private setRoutes() {
    if (this.featureDisabled) {
      this.routes = routeTableSampleRoutes;
      this.totalRoutes = this.routes.length;
      this.totalPages = Math.ceil(this.totalRoutes / this.PAGE_SIZE);
      return;
    }

    const params = {
      projectId: this.project.id,
      time__gte: this.roundTimeService.timestampFor(this.timeDurAgo, this.dur),
      page: this.currentPage,
      limit: this.PAGE_SIZE,
    } as any;

    const order = this.orderBy();
    if (order) {
      params.order = order;
    }
    if (this.routeSearch) {
      params.route__ieq = this.routeSearch;
    }
    if (this.httpMethod) {
      params.method = this.httpMethod;
    }
    if (this.environment !== undefined) {
      params.environment = this.environment;
    }

    this.loadingRoutes = true;
    this.loadingChanged.emit(1);

    this.routesService
      .get(params)
      .pipe(
        finalize(() => {
          this.loadingRoutes = false;
          this.loadingChanged.emit(-1);
        }),
      )
      .subscribe((data: any) => {
        this.totalRoutes = data.count;
        this.totalPages = Math.ceil(this.totalRoutes / this.PAGE_SIZE);

        let routes = this.attachRequestRates(data.routes);
        routes = this.attachResponseCodesBarData(routes);
        routes = this.attachResponseTimeClasses(routes);
        this.routes = this.sortRoutes(routes, this.sortColumn, this.sortDesc);
      });
  }

  private sortRoutes(routes: any[], column: string, sortDesc: boolean) {
    return orderBy(routes, [column], [sortDesc ? "desc" : "asc"]);
  }

  private setRoutesStats() {
    if (this.featureDisabled) {
      this.routesStats = routeTableSampleRoutesStats;
      this.totalStatus = routeTableSampleTotalStatus;
      return;
    }

    const period = (() => {
      switch (this.dur.split("/")[1]) {
        case "1d":
          return "day";
        case "1h":
          return "hour";
        case "1m":
          return "minute";
      }
    })();

    const params = {
      projectId: this.project.id,
      period,
      time__gte: this.roundTimeService.timestampFor(this.timeDurAgo, this.dur),
    } as any;

    if (this.apdexT > 0) {
      params.t = this.apdexT;
    }
    if (this.httpMethod) {
      params.method = this.httpMethod;
    }
    if (this.environment !== undefined) {
      params.environment = this.environment;
    }

    this.loadingRoutesStats = true;
    this.loadingChanged.emit(1);
    this.routesStatsService
      .all(params)
      .pipe(
        finalize(() => {
          this.loadingRoutesStats = false;
          this.loadingChanged.emit(-1);
        }),
      )
      .subscribe((data: any) => {
        this.routesStats = data;
        const countLists = {
          requests: data.total.count,
          notices: data.noticeCount,
          time: data.time,
        };
        this.errorRateList = this.errorRateListService.statsFor(
          countLists,
          this.dur,
          this.lastN,
        );
        this.totalStatus = {
          name: "total",
          value: "total",
          ...this.routesStats.total,
        };
      });
  }

  private defaultSortDesc(column: string) {
    // Default sort for route is ascending and for others descending.
    return column !== "route";
  }

  private firstChange(binding: any) {
    if (binding && binding.isFirstChange()) {
      return true;
    } else {
      return false;
    }
  }
}
